/**
 * FilmstripSlideGap Component
 *
 * A compact vertical-stacked hover zone between slides in the filmstrip.
 * Combines Insert + Transition buttons in a single column to reduce horizontal footprint.
 *
 * Layout on hover:
 * ┌─────┐
 * │  +  │  ← Add slide (top)
 * ├─────┤
 * │  ⊗  │  ← Transition (bottom)
 * └─────┘
 */

import React, { useState, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Plus, Sparkles, Zap, Square, ArrowLeft, ArrowRight, ArrowUp, ArrowDown, ChevronRight, ChevronLeft, Circle, ZoomIn, ZoomOut, Loader } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { TransitionSelector } from './TransitionSelector';
import {
  TransitionConfig,
  TransitionType,
  getTransitionOption,
} from '@/types/videoManifest';

export interface FilmstripSlideGapProps {
  /** Called when the insert button is clicked */
  onInsert: () => void;
  /** The transition config for this slide (null = uses default) */
  transition?: TransitionConfig | null;
  /** The default transition for the video */
  defaultTransition?: TransitionConfig;
  /** Called when user updates the transition */
  onTransitionUpdate?: (transition: TransitionConfig) => void;
  /** Called when user clears the custom transition (reverts to default) */
  onTransitionClear?: () => void;
  /** URL for the "from" slide thumbnail */
  fromThumbnail?: string;
  /** URL for the "to" slide thumbnail */
  toThumbnail?: string;
  /** Whether the transition is currently being saved */
  isSaving?: boolean;
  /** Whether to show the transition button (only between real slides with manifest) */
  showTransition?: boolean;
  /** Additional class names */
  className?: string;
}

/**
 * Get the icon component for a transition type
 */
const getTransitionIcon = (type: TransitionType): React.ReactNode => {
  const iconClass = 'w-2.5 h-2.5';

  switch (type) {
    case 'crossfade':
    case 'fade':
      return <Sparkles className={iconClass} />;
    case 'cut':
    case 'none':
      return <Zap className={iconClass} />;
    case 'fade-black':
      return <Square className={cn(iconClass, 'fill-current')} />;
    case 'slide-left':
      return <ArrowLeft className={iconClass} />;
    case 'slide-right':
      return <ArrowRight className={iconClass} />;
    case 'slide-up':
      return <ArrowUp className={iconClass} />;
    case 'slide-down':
      return <ArrowDown className={iconClass} />;
    case 'wipe-left':
      return <ChevronRight className={iconClass} />;
    case 'wipe-right':
      return <ChevronLeft className={iconClass} />;
    case 'circle-reveal':
      return <Circle className={iconClass} />;
    case 'zoom-in':
      return <ZoomIn className={iconClass} />;
    case 'zoom-out':
      return <ZoomOut className={iconClass} />;
    case 'blur-fade':
      return <Loader className={iconClass} />;
    case 'content-exit':
    case 'stagger-cascade':
    case 'stagger-random':
      return <Sparkles className={iconClass} />;
    default:
      return <Sparkles className={iconClass} />;
  }
};

export const FilmstripSlideGap: React.FC<FilmstripSlideGapProps> = ({
  onInsert,
  transition,
  defaultTransition,
  onTransitionUpdate,
  onTransitionClear,
  fromThumbnail,
  toThumbnail,
  isSaving = false,
  showTransition = false,
  className,
}) => {
  const [isTransitionOpen, setIsTransitionOpen] = useState(false);

  // Use custom transition or fall back to default
  const effectiveTransition = transition || defaultTransition;
  const isCustom = !!transition;

  // Handle transition update
  const handleApply = useCallback((newTransition: TransitionConfig) => {
    onTransitionUpdate?.(newTransition);
    setIsTransitionOpen(false);
  }, [onTransitionUpdate]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    setIsTransitionOpen(false);
  }, []);

  return (
    <div
      className={cn(
        'group/gap relative flex-shrink-0 w-3 h-14',
        'flex flex-col items-center justify-center',
        className
      )}
    >
      {/* Vertical divider line (visible on hover) */}
      <div className="absolute inset-y-1 left-1/2 -translate-x-1/2 w-px bg-zinc-600/0 group-hover/gap:bg-zinc-600/30 transition-colors" />

      {/* Stacked buttons container */}
      <div
        className={cn(
          'relative z-10 flex flex-col items-center',
          showTransition ? 'gap-0.5' : 'gap-0',
          'opacity-0 group-hover/gap:opacity-100 transition-opacity duration-150'
        )}
      >
        {/* Insert button (top) */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onInsert();
          }}
          className={cn(
            'flex items-center justify-center',
            'w-5 h-5 rounded-full',
            'bg-primary text-primary-foreground',
            'shadow-md',
            'scale-75 group-hover/gap:scale-100 transition-transform duration-150',
            'hover:bg-primary/90 hover:scale-110'
          )}
          title="Add slide"
        >
          <Plus className="w-3 h-3" />
        </button>

        {/* Transition button (bottom) - only if showTransition */}
        {showTransition && effectiveTransition && (
          <Popover open={isTransitionOpen} onOpenChange={setIsTransitionOpen}>
            <PopoverTrigger asChild>
              <button
                className={cn(
                  'flex items-center justify-center',
                  'w-5 h-5 rounded-full',
                  'shadow-md backdrop-blur-sm',
                  'scale-75 group-hover/gap:scale-100 transition-all duration-150',
                  isCustom
                    ? 'bg-primary/90 text-primary-foreground hover:bg-primary'
                    : 'bg-white/80 dark:bg-zinc-700/80 text-zinc-600 dark:text-zinc-300 hover:bg-white dark:hover:bg-zinc-600',
                  'hover:scale-110',
                  isTransitionOpen && 'opacity-100 scale-100',
                  isSaving && 'animate-pulse'
                )}
                title={`Transition: ${getTransitionOption(effectiveTransition.type)?.label || effectiveTransition.type}`}
              >
                {isSaving ? (
                  <Loader className="w-2.5 h-2.5 animate-spin" />
                ) : (
                  getTransitionIcon(effectiveTransition.type)
                )}
              </button>
            </PopoverTrigger>

            <PopoverContent
              className="w-80 p-4"
              side="top"
              align="center"
              sideOffset={8}
            >
              <TransitionSelector
                currentTransition={effectiveTransition}
                onApply={handleApply}
                onCancel={handleCancel}
                fromThumbnail={fromThumbnail}
                toThumbnail={toThumbnail}
              />

              {/* Clear custom transition button */}
              {isCustom && onTransitionClear && (
                <div className="mt-3 pt-3 border-t">
                  <button
                    onClick={() => {
                      onTransitionClear();
                      setIsTransitionOpen(false);
                    }}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Reset to default transition
                  </button>
                </div>
              )}
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );
};

export default FilmstripSlideGap;
